import { Component, ElementRef, HostBinding, ViewChild } from '@angular/core';
import { ContextMenuOption } from '../../../../models/context-menu-option';
import { Endpoint, HelpIndex, SettingsType, UpdateStatusType, MessageType, MembershipType } from '../../../../models/enums';
import { AlignDirection, CellTemplateType, GenericColDef, GridContext, SortByFilter, FilterTemplateType } from '../../../../models/grid-context';
import * as _ from 'underscore';
import { SettingsNavigationContentComponent } from '../../../../models/settings-navigation-content-component';
import { CommonService } from '../../../../services/common/common.service';
import { RestService } from '../../../../services/rest/rest.service';
import { GenericTreeNode } from '../../../generic-tree/generic-tree-node';
import { DeleteEntityModalComponent } from '../../delete-entity-modal/delete-entity-modal.component';
import { MembershipModalComponent } from '../../membership-modal/membership-modal.component';
import { GenericGridComponent } from '../../../generic-grid/generic-grid.component';
import { ActionType, ObjectActionListType, PermissionSet, Permissions } from '../../../../models/permission';
import { SysOrganization } from '../../../../models/system-organization';
import { NewOrganizationModalComponent } from '../../new-organization-modal/new-organization-modal.component';
import { Role } from '../../../../models/role';
import { createOrgNameOrIdRoleHandles, isNullOrUndefined } from '../../../../models/utils';
import { CreateEditEntityModalComponent } from '../../create-edit-entity-modal/create-edit-entity-modal.component';
import { Organization } from '../../../../models/organization';

@Component({
    standalone: false,
    selector: 'res-organizations',
    templateUrl: './organizations.component.html',
    styleUrls: ['./organizations.component.scss']
})
export class OrganizationsComponent implements SettingsNavigationContentComponent {
    @HostBinding('class') classes = 'maximized-component';
    @ViewChild('downloadLink', { read: ElementRef, static: true }) downloadLink: ElementRef;
    @ViewChild(GenericGridComponent, { static: true }) genericGrid: GenericGridComponent;

    automationId = 'organizations-list';
    isDirty: boolean;
    validChanges: boolean;
    gridName = 'Organizations';

    isBusy = false;
    isBusyMessage = 'Loading Organizations...';
    helpIndex = HelpIndex.sys_org_summary_dlg;
    updateStatusType = UpdateStatusType.ORGANIZATION;
    newOrganization = 'New Organization ...';

    // permissions
    hasOrganizationAddEditPermissions: boolean;
    hasContextMenuPermissions = true;

    allUsers = [];
    roleList = [];
    organizations: Array<any> = [];
    gridContext = new GridContext({
        sortTypeEnabled: true,
        sortEnabled: true,
        sortAscending: true,
        sortAscendingFieldName: 'descending',
        sortByFieldName: 'sortByFilter',
        sortBy: SortByFilter.NAME
    });

    requestInfo = {
        endpoint: Endpoint.realmManager_listOrganizations,
        responseName: 'organizations',
        data: { count: 0, startIndex: 0, systemScope: true, filters: [] }
    };

    columnDefs = new Array<GenericColDef>(
        {
            headerName: 'Organization Name',
            field: 'name',
            automationId: this.automationId,
            minWidth: 60,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            sortBy: SortByFilter.NAME,
            filterType: FilterTemplateType.comboBox,
            filterAttributeName: "NAME",
            suppressMovable: true,
            lockVisible: true,
            serviceTierFilter: true,
        },
        {
            headerName: 'Description',
            field: 'description',
            automationId: this.automationId,
            minWidth: 60,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            filterType: FilterTemplateType.freeform,
            filterAttributeName: "ORG_DESCRIPTION",
            suppressMovable: true,
            lockVisible: true,
            serviceTierFilter: true,
        },
        {
            headerName: 'Storage Used',
            field: 'storageUsed',
            automationId: this.automationId,
            minWidth: 60, width: 100,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            sortBy: "USAGE",
        },
        {
            headerName: 'Date Last Calculated',
            field: 'lastCalculated',
            automationId: this.automationId,
            minWidth: 60, width: 145,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            sortBy: "LAST_CALCULATE",
        },
        {
            headerName: 'Date Created',
            field: 'createdOn',
            automationId: this.automationId,
            minWidth: 60, width: 145,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            cellTemplateType: CellTemplateType.date,
            sortBy: SortByFilter.DATE_CREATED,
            suppressMovable: true,
            serviceTierFilter: true,
            filterAttributeName: "CREATED_TIME",
            filterType: FilterTemplateType.date,
        },
        {
            headerName: 'Date Last Accessed',
            field: 'lastAccessedDate',
            automationId: this.automationId,
            minWidth: 60, width: 145,
            cellTemplateType: CellTemplateType.date,
            suppressMovable: true,
            headerAlignDirection: AlignDirection.right,
            cellAlignDirection: AlignDirection.right,
            filterType: FilterTemplateType.date,
            serviceTierFilter: true,
            filterAttributeName: "LAST_ACCESSED_TIME",
        },
        {
            headerName: 'Created By',
            field: 'createdBy',
            automationId: this.automationId,
            minWidth: 60,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            sortBy: "CREATED_BY",
        },
        {
            headerName: 'Status',
            field: 'deletePending',
            automationId: this.automationId,
            minWidth: 60,
            cellTemplateType: CellTemplateType.basicLabelWithTooltip,
            suppressMovable: true,
            sortBy: "STATUS",
        },
        {
            automationId: this.automationId,
            cellTemplateType: CellTemplateType.contextMenu,
            minWidth: 30, maxWidth: 30, width: 30,
            suppressSizeToFit: true
        }
    );

    contextMenuOptions = [
        new ContextMenuOption({
            nameFunction: (item) => `Enable All Projects`,
            iconClass: 'fa-book',
            iconColor: 'color-blue-01',
            automationId: 'organizations-enable-all',
            click: (item) => this.changeAll(item, true)
        }),
        new ContextMenuOption({
            nameFunction: (item) => `Disable All Projects`,
            iconClass: 'fa-book',
            iconColor: 'color-grey-08',
            automationId: 'organizations-disable-all',
            click: (item) => this.changeAll(item, false)
        }),
        new ContextMenuOption({
            divider: true
        }),
        new ContextMenuOption({
            name: 'Edit ...',
            click: (item) => this.editOrganization(item),
            iconClass: 'fa-pencil',
            iconColor: 'color-blue-01'
        }),
        new ContextMenuOption({
            name: 'Add/Remove Members ...',
            iconClass: 'fa-users',
            iconColor: 'color-blue-01',
            automationId: 'organizations-add-remove-members',
            click: (item) => this.addRemoveMembers(item),
            enabled: (item) => item && item?.deletePending === 'Available',
        }),
        new ContextMenuOption({
            divider: true
        }),
        new ContextMenuOption({
            name: 'Delete Organization', iconClass: 'fa-trash', iconColor: 'color-red-05', automationId: 'organizations-delete-project',
            click: (item) => this.deleteOrganization(item),
            permissions: [new PermissionSet({ permissionType: ObjectActionListType.ORGANIZATIONS, permissionActions: [ActionType.DELETE] })]
        })
    ];

    constructor(private commonService: CommonService, private restService: RestService) {
        this.loadOrganizationsDropdown();
    }

    loadOrganizationsDropdown(): void {
        const promiseList = [];
        const orgNameColDef = this.columnDefs.find(f => f.field === 'name');
        if (orgNameColDef) {
            promiseList.push(this.restService.post(Endpoint.realmManager_listOrganizations, { count: 0, startIndex: 0, filters: [], systemScope: true }).then(response => {
                if (response.organizations) {
                    orgNameColDef.filterItems = response.organizations.map(q => q.name).sort();
                    if (this.genericGrid) {
                        this.genericGrid.setColumnDefs(this.columnDefs);
                        this.genericGrid.refreshColumns();
                        this.isBusy = false;
                    }
                }
            }));
        }
    }

    gridPagerCustomCallback = () => {
        this.loadOrganizationsDropdown();
    }

    responseCallback = (response: any) => {
        this.organizations = response?.organizations;
    }

    responseArrayCallback = (value: any) => {
        if (value?.createdOn?.length > 0) {
            value.createdOn = this.commonService.insertYesterday(value.createdOn);
        }
        if (value?.dateLastAccessed?.length > 0) {
            value.dateLastAccessed = this.commonService.insertYesterday(value.dateLastAccessed);
        }
        value.deletePending = value.deletePending === "Available" || !value.deletePending ? "Available" : "Delete Pending";
        value.lastCalculated = value?.storageUsages[0]?.usageInKb === 'Not Calculated' ? 'Never' : this.commonService.insertYesterday(value?.storageUsages[0]?.lastCalculated);
        if (value.storageUsages) {
            const usageInKb0 = value?.storageUsages[0]?.usageInKb;
            const usageInKb1 = value?.storageUsages[1]?.usageInKb;
            const usageInKb2 = value?.storageUsages[2]?.usageInKb;
            const storageUsed = parseInt(usageInKb0 !== 'Not Calculated' && usageInKb0 ? usageInKb0 : '0') +
                parseInt(usageInKb1 !== 'Not Calculated' && usageInKb1 ? usageInKb1 : '0') +
                parseInt(usageInKb2 !== 'Not Calculated' && usageInKb2 ? usageInKb2 : '0');
            value.storageUsed = value?.storageUsages[0]?.usageInKb == 'Not Calculated' ? '' : this.commonService.formatSizeUnits(storageUsed * 1024);
        }
        return value;
    }

    download(): void {
        this.calculateOrDownload('download');
    }

    parseUserOrGroup(entity: any, systemScope = false): any {
        if (entity) {
            if (entity.userName) {
                entity.type = entity.importedFromSystemUser || systemScope ? 'System User' : 'User';
                entity.iconClass = `fa-user ${entity.importedFromSystemUser || systemScope ? ' color-purple-03' : ' color-yellow-07'}`;
                entity.name = entity.userName;
            } else {
                entity.type = entity.importedFromSystemGroup || systemScope ? 'System Group' : 'Group';
                entity.iconClass = `fa-users ${entity.importedFromSystemGroup || systemScope ? ' color-purple-03' : ' color-yellow-07'}`;
            }
            if (this.roleList) {
                entity.selectedRole = this.roleList.filter(r => r.handle === entity.roles[0]?.handle).pop();;
                entity.roleList = this.roleList;
                entity.initialRole = isNullOrUndefined(this.getUserRole(entity.roleList)) ? this.roleList.find(r => r.name === Role.organizationAdministrator) : this.getUserRole(entity.roleList);
                if (!entity.selectedRole) {
                    entity.selectedRole = entity.initialRole;
                }
            }
        }
    }

    getUserRole(roleHandles: Array<string>): any {
        if (roleHandles && roleHandles.length > 0) {
            return this.roleList.filter(r => r.handle === roleHandles[0]).pop();
        }
        return null;
    }

    changeAll(organization: any, enable: boolean): void {
        this.restService.post(Endpoint.orgManager_updateProjectsState,
            { organizationHandle: organization.name, projectState: enable ? 'ACTIVE' : 'DISABLED', systemScope: true });
    }

    addRemoveMembers(organization): void {
        if (organization) {
            this.isBusy = true;
            this.isBusyMessage = 'Loading ...';
            this.restService.post(Endpoint.adminOrgManager_listRoles, {
                objectType: 'ORGANIZATION',
                organizationName: organization.name,
                systemScope: true
            }).then(responseListRoles => {
                this.roleList = responseListRoles?.roles;

                this.restService.post(Endpoint.adminOrgManager_listUsersAndGroups, { organizationName: organization.name, onlyGroups: false, onlyUsers: false, systemScope: true }).then(responseListUsersAndGroups => {
                    if (responseListUsersAndGroups) {
                        this.allUsers = [];
                        const members = [];
                        if (responseListUsersAndGroups.groups) {
                            responseListUsersAndGroups.groups.forEach(entity => {
                                this.parseUserOrGroup(entity);
                                this.allUsers.push(entity);
                                members.push(entity);
                            });
                        }
                        if (responseListUsersAndGroups.users) {
                            responseListUsersAndGroups.users.forEach(entity => {
                                this.parseUserOrGroup(entity);
                                this.allUsers.push(entity);
                                members.push(entity);
                            });
                        }
                        this.restService.post(Endpoint.adminOrgManager_listUsersAndGroups, { organizationName: 'super_system_customer', onlyGroups: false, onlyUsers: false, systemScope: true }).then(responseListSystemUsersAndGroups => {
                            if (responseListSystemUsersAndGroups) {
                                if (responseListSystemUsersAndGroups?.groups) {
                                    responseListSystemUsersAndGroups.groups?.forEach(entity => {
                                        const foundGroupIndex = this.allUsers?.findIndex(item => item?.name === entity?.name);
                                        if (foundGroupIndex === -1) {
                                            if (!entity.importedFromSystemGroup) {
                                                this.parseUserOrGroup(entity, true);
                                                this.allUsers.push(entity);
                                            }
                                        } else {
                                            this.allUsers.splice(foundGroupIndex, 1);
                                        }
                                    });
                                }
                                if (responseListSystemUsersAndGroups?.users) {
                                    responseListSystemUsersAndGroups.users?.forEach(entity => {
                                        const foundUserIndex = this.allUsers?.findIndex(item => item?.userName === entity?.userName)
                                        if (foundUserIndex === -1 && !entity.importedFromSystemUser) {
                                            this.parseUserOrGroup(entity, true);
                                            this.allUsers.push(entity);
                                        } else {
                                            this.allUsers.splice(foundUserIndex, 1);
                                        }
                                    });
                                }
                            }
                            this.isBusy = false;
                            this.commonService.openModal(MembershipModalComponent, {
                                class: 'vertical-percent-80',
                                modalData: { leftList: this.allUsers, rightList: members.filter(a => a.importedFromSystemUser || a.importedFromSystemGroup).sort(this.compare), membershipType: MembershipType.organization, isSystemSettingsProjects: true, isSystemSettingsContext: true, organizationName: organization?.name, roleOptions: this.roleList, isSystemGroupsOrSystemOrgs: true }
                            });
                        });
                    }
                }, () => {
                    this.isBusy = false;
                });
            }, () => {
                this.isBusy = false;
            });
        }
    }

    private compare(a: any, b: any) {
        const aName = a.name ? a.name.toLowerCase() : a.userName.toLowerCase();
        const bName = b.name ? b.name.toLowerCase() : b.userName.toLowerCase();
        if (aName < bName) {
            return -1;
        } else if (aName > bName) {
            return 1;
        }
        return 0;
    }

    addNewOrganization(): void {
        this.commonService.openModal(NewOrganizationModalComponent,
            { backdrop: false, modalData: {}, class: 'vertical-percent-60' }).then((result) => {
                if (result.modalResult) {
                    this.genericGrid.loadData();
                    this.commonService.sendMessage(MessageType.project);
                }
            });
    }

    calculateStorage(): void {
        this.isBusy = true;
        this.isBusyMessage = 'Calculating Storage ...';
        this.calculateOrDownload('calculate');
    }

    deleteOrganization(org): void {
        this.commonService.openModal(DeleteEntityModalComponent, { modalData: new SysOrganization(org), options: { deleteOrganizationsFomSystemSettings: true } }).then(response => {
            if (response.modalResult) {
                setTimeout(() => {
                    this.genericGrid.loadData();
                }, 3000);
            }
        });
    }

    editOrganization(org): void {
        const orgObject = new Organization(org);
        this.commonService.openModal(CreateEditEntityModalComponent, { modalData: orgObject }).then(response => {
            if (response.modalResult) {
                this.genericGrid.loadData();
                this.commonService.sendMessage(MessageType.project);
            }
        });
    }

    saveChanges(): Promise<boolean> {
        return Promise.resolve(true);
    }

    selectedTreeItemChanged(node: GenericTreeNode, projectHandle?: string, settingsType?: SettingsType): void {
        this.hasOrganizationAddEditPermissions = Permissions.checkUserPermission(ObjectActionListType.ORGANIZATIONS, ActionType.EDIT);
        if (this.columnDefs.find(c => c.cellTemplateType === CellTemplateType.contextMenu)) {
            if (!this.hasOrganizationAddEditPermissions) {
                this.columnDefs = this.columnDefs.filter(col => col.cellTemplateType !== CellTemplateType.contextMenu);
                this.hasContextMenuPermissions = false;
            }
        }
        return;
    }

    calculateOrDownload(type?: string): void {
        if (type === 'download') {
            this.restService.post(Endpoint.realmManager_getStorageUsageDownloadUrl, { systemScope: true }).then(response => {
                if (response?.url) {
                    this.commonService.unzipFile(response.url, this.downloadLink.nativeElement, 'OrganizationsManifest');
                    this.isBusy = false;
                }
            })
        } else if (type === 'calculate') {
            this.restService.post(Endpoint.realmManager_calculateStorageUsage, {}, true, [MessageType.entityChanged, this.updateStatusType]).then(response => {
                this.genericGrid.loadData();
                this.isBusy = false;
            });
        }
    }

}
